import * as React from "react";
import {
  Box,
  Typography,
  Button as MUIButton,
  Paper,
  Modal,
  Backdrop,
  Fade,
  CircularProgress,
  Chip,
  MenuItem,
} from "@mui/material";
import { notification } from "antd";
import { FoTable, statusBadge } from "./financeUi";
import { connect } from "react-redux";
import moment from "moment";
import { MainInput, main_button, sec_button } from "../../app/components/Styles";
import {
  getSubscriptionWaivers,
  createSubscriptionWaiver,
  updateSubscriptionWaiver,
  revokeSubscriptionWaiver,
  checkSubscriptionWaiver,
} from "../../app/store/actions/whFinanceActions";
import { CardGiftcard, Edit, Block } from "@mui/icons-material";
import {
  toYearMonth,
  formatMonthLabel,
  formatRangeLabel,
  isValidMonthRange,
} from "./subscriptionWaiverHelpers";
import MonthYearSelect from "./MonthYearSelect";

const SUBSCRIPTION_TYPES = ["Backtrack", "Pulse"];

const emptyForm = {
  subscriptionType: "Backtrack",
  startMonth: null,
  endMonth: null,
  notes: "",
};

export const SubscriptionWaivers = ({
  vendorId,
  getSubscriptionWaivers,
  createSubscriptionWaiver,
  updateSubscriptionWaiver,
  revokeSubscriptionWaiver,
  checkSubscriptionWaiver,
  subscriptionWaivers,
  isWaiting,
}) => {
  const [formModalOpen, setFormModalOpen] = React.useState(false);
  const [revokeModalOpen, setRevokeModalOpen] = React.useState(false);
  const [editingWaiver, setEditingWaiver] = React.useState(null);
  const [waiverToRevoke, setWaiverToRevoke] = React.useState(null);
  const [formData, setFormData] = React.useState(emptyForm);
  const [submitting, setSubmitting] = React.useState(false);
  const [listLoading, setListLoading] = React.useState(false);
  const [activeChecks, setActiveChecks] = React.useState({});
  const [showInactive, setShowInactive] = React.useState(false);

  const currentMonth = moment().format("YYYY-MM");

  const loadWaivers = React.useCallback(async () => {
    if (!vendorId) return;
    setListLoading(true);
    try {
      await getSubscriptionWaivers({ vendorId });
    } catch {
      // Errors already toasted in the action
    } finally {
      setListLoading(false);
    }
  }, [vendorId, getSubscriptionWaivers]);

  const loadCurrentMonthBadges = React.useCallback(async () => {
    if (!vendorId) return;
    try {
      const results = await Promise.all(
        SUBSCRIPTION_TYPES.map((subscriptionType) =>
          checkSubscriptionWaiver({
            vendorId,
            subscriptionType,
            month: currentMonth,
          }).then((data) => ({ subscriptionType, data }))
        )
      );
      const next = {};
      results.forEach(({ subscriptionType, data }) => {
        next[subscriptionType] = Boolean(data?.waived);
      });
      setActiveChecks(next);
    } catch {
      // Errors already toasted in the action
    }
  }, [vendorId, checkSubscriptionWaiver, currentMonth]);

  React.useEffect(() => {
    loadWaivers();
    loadCurrentMonthBadges();
  }, [loadWaivers, loadCurrentMonthBadges]);

  const waiversList = React.useMemo(() => {
    const list = Array.isArray(subscriptionWaivers)
      ? subscriptionWaivers
      : subscriptionWaivers?.waivers || [];
    if (showInactive) return list;
    return list.filter((w) => w.isActive !== false);
  }, [subscriptionWaivers, showInactive]);

  const openCreateModal = () => {
    setEditingWaiver(null);
    setFormData(emptyForm);
    setFormModalOpen(true);
  };

  const openEditModal = (waiver) => {
    setEditingWaiver(waiver);
    setFormData({
      subscriptionType: waiver.subscriptionType || "Backtrack",
      startMonth: toYearMonth(waiver.startMonth),
      endMonth: toYearMonth(waiver.endMonth),
      notes: waiver.notes || "",
    });
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    if (submitting) return;
    setFormModalOpen(false);
    setEditingWaiver(null);
    setFormData(emptyForm);
  };

  const validateForm = () => {
    if (!formData.subscriptionType) {
      notification.error({ message: "Please select a subscription type" });
      return false;
    }
    if (!formData.startMonth || !formData.endMonth) {
      notification.error({ message: "Please select start and end months" });
      return false;
    }
    if (!isValidMonthRange(formData.startMonth, formData.endMonth)) {
      notification.error({ message: "End month must be on or after start month" });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !vendorId) return;
    setSubmitting(true);
    const payload = {
      subscriptionType: formData.subscriptionType,
      startMonth: formData.startMonth,
      endMonth: formData.endMonth,
      notes: formData.notes?.trim() || undefined,
    };
    try {
      if (editingWaiver?._id) {
        await updateSubscriptionWaiver({ id: editingWaiver._id, ...payload });
      } else {
        await createSubscriptionWaiver({ vendorId, ...payload });
      }
      setFormModalOpen(false);
      setEditingWaiver(null);
      setFormData(emptyForm);
      loadWaivers();
      loadCurrentMonthBadges();
    } catch {
      // Errors already toasted
    } finally {
      setSubmitting(false);
    }
  };

  const openRevokeModal = (waiver) => {
    setWaiverToRevoke(waiver);
    setRevokeModalOpen(true);
  };

  const closeRevokeModal = () => {
    if (submitting) return;
    setRevokeModalOpen(false);
    setWaiverToRevoke(null);
  };

  const handleRevoke = async () => {
    if (!waiverToRevoke?._id) return;
    setSubmitting(true);
    try {
      await revokeSubscriptionWaiver(waiverToRevoke._id);
      setRevokeModalOpen(false);
      setWaiverToRevoke(null);
      loadWaivers();
      loadCurrentMonthBadges();
    } catch {
      // Errors already toasted
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: "Type",
      dataIndex: "subscriptionType",
      key: "subscriptionType",
      render: (text) => text || "—",
    },
    {
      title: "Free from",
      dataIndex: "startMonth",
      key: "startMonth",
      render: (value) => formatMonthLabel(value),
    },
    {
      title: "Free until",
      dataIndex: "endMonth",
      key: "endMonth",
      render: (value) => formatMonthLabel(value),
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      render: (text) => text || "—",
      ellipsis: true,
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive) => statusBadge(isActive !== false ? "Active" : "Inactive"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <MUIButton
            size="small"
            startIcon={<Edit fontSize="small" />}
            sx={{ ...sec_button, textTransform: "none", minWidth: 0 }}
            onClick={() => openEditModal(record)}
            disabled={record.isActive === false}
          >
            Edit
          </MUIButton>
          {record.isActive !== false && (
            <MUIButton
              size="small"
              startIcon={<Block fontSize="small" />}
              sx={{ textTransform: "none", color: "error.main", minWidth: 0 }}
              onClick={() => openRevokeModal(record)}
            >
              Revoke
            </MUIButton>
          )}
        </Box>
      ),
    },
  ];

  const waivedTypes = SUBSCRIPTION_TYPES.filter((type) => activeChecks[type]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        backgroundColor: "rgba(0, 0, 0, 0.02)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <CardGiftcard sx={{ color: "var(--secColor)" }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Free subscriptions
            </Typography>
          </Box>
          {waivedTypes.length > 0 ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {waivedTypes.map((type) => (
                <Chip
                  key={type}
                  size="small"
                  label={`${type} free this month`}
                  sx={{
                    backgroundColor: "rgba(76, 175, 80, 0.12)",
                    color: "#2e7d32",
                    fontWeight: 500,
                  }}
                />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No free subscriptions for {moment(currentMonth, "YYYY-MM").format("MMM YYYY")}.
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <MUIButton
            size="small"
            sx={{ textTransform: "none" }}
            onClick={() => setShowInactive((prev) => !prev)}
          >
            {showInactive ? "Hide inactive" : "Show inactive"}
          </MUIButton>
          <MUIButton
            sx={{ ...main_button, textTransform: "none" }}
            onClick={openCreateModal}
          >
            Make subscription free
          </MUIButton>
        </Box>
      </Box>

      <FoTable
        columns={columns}
        dataSource={waiversList}
        loading={listLoading}
        rowKey={(row) => row._id}
        empty="No subscription waivers for this brand."
        pagination={{ pageSize: 5, total: waiversList.length }}
      />

      <Modal
        open={formModalOpen}
        onClose={closeFormModal}
        closeAfterTransition
        disableEnforceFocus
        disableAutoFocus
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Fade in={formModalOpen}>
          <Paper
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "90%", sm: 480 },
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              outline: "none",
            }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 3, fontWeight: 600, color: "var(--secColor)" }}
            >
              {editingWaiver ? "Edit free subscription" : "Make subscription free"}
            </Typography>

            <Box sx={{ mb: 2.5 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: "text.secondary" }}>
                Subscription type *
              </Typography>
              <MainInput
                select
                fullWidth
                value={formData.subscriptionType}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    subscriptionType: e.target.value,
                  }))
                }
                disabled={submitting || isWaiting}
              >
                {SUBSCRIPTION_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </MainInput>
            </Box>

            <Box sx={{ mb: 2.5 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: "text.secondary" }}>
                Start month *
              </Typography>
              <MonthYearSelect
                value={formData.startMonth}
                onChange={(startMonth) =>
                  setFormData((prev) => ({
                    ...prev,
                    startMonth,
                    // Keep end valid if start moves past it
                    endMonth:
                      prev.endMonth && startMonth && prev.endMonth < startMonth
                        ? startMonth
                        : prev.endMonth,
                  }))
                }
                disabled={submitting || isWaiting}
              />
            </Box>

            <Box sx={{ mb: 2.5 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: "text.secondary" }}>
                End month *
              </Typography>
              <MonthYearSelect
                value={formData.endMonth}
                onChange={(endMonth) =>
                  setFormData((prev) => ({
                    ...prev,
                    endMonth,
                  }))
                }
                minMonth={formData.startMonth}
                disabled={submitting || isWaiting}
              />
            </Box>

            {formData.startMonth && formData.endMonth && formData.endMonth >= formData.startMonth && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                Range: {formatRangeLabel(formData.startMonth, formData.endMonth)}
              </Typography>
            )}

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: "text.secondary" }}>
                Notes (optional)
              </Typography>
              <MainInput
                fullWidth
                multiline
                minRows={2}
                value={formData.notes}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, notes: e.target.value }))
                }
                disabled={submitting || isWaiting}
                placeholder="Optional note"
              />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
              <MUIButton
                onClick={closeFormModal}
                disabled={submitting}
                sx={{ textTransform: "none" }}
              >
                Cancel
              </MUIButton>
              <MUIButton
                onClick={handleSubmit}
                disabled={submitting || isWaiting}
                sx={{ ...main_button, textTransform: "none" }}
              >
                {submitting ? (
                  <CircularProgress size={20} sx={{ color: "white" }} />
                ) : editingWaiver ? (
                  "Save changes"
                ) : (
                  "Create waiver"
                )}
              </MUIButton>
            </Box>
          </Paper>
        </Fade>
      </Modal>

      <Modal
        open={revokeModalOpen}
        onClose={closeRevokeModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Fade in={revokeModalOpen}>
          <Paper
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "90%", sm: 420 },
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              outline: "none",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: "error.main" }}>
              Revoke free subscription
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
              Revoke {waiverToRevoke?.subscriptionType} free for{" "}
              {formatRangeLabel(waiverToRevoke?.startMonth, waiverToRevoke?.endMonth)}?
              This marks the waiver inactive.
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
              <MUIButton
                onClick={closeRevokeModal}
                disabled={submitting}
                sx={{ textTransform: "none" }}
              >
                Cancel
              </MUIButton>
              <MUIButton
                onClick={handleRevoke}
                disabled={submitting || isWaiting}
                sx={{
                  ...main_button,
                  textTransform: "none",
                  bgcolor: "error.main",
                  "&:hover": { bgcolor: "error.dark" },
                }}
              >
                {submitting ? (
                  <CircularProgress size={20} sx={{ color: "white" }} />
                ) : (
                  "Revoke"
                )}
              </MUIButton>
            </Box>
          </Paper>
        </Fade>
      </Modal>
    </Paper>
  );
};

const mapStateToProps = (state) => ({
  subscriptionWaivers: state?.records?.subscriptionWaivers,
  isWaiting: state?.wait?.isLoading,
});

const mapDispatchToProps = {
  getSubscriptionWaivers,
  createSubscriptionWaiver,
  updateSubscriptionWaiver,
  revokeSubscriptionWaiver,
  checkSubscriptionWaiver,
};

export default connect(mapStateToProps, mapDispatchToProps)(SubscriptionWaivers);
